import { HTMLAttributes, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import Draggable from 'gsap/dist/Draggable'
import mergeRefs from 'merge-refs'
import Image from 'next/image'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Section from '@/components/base/section'
import SVGIcon from '@/components/base/svg-icon'

import { useBeforeAfterMotion } from './motion'
import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

export type BeforeAfterProps = HTMLAttributes<HTMLDivElement> & {
  image_1: WPImage
  image_2: WPImage
}

const BeforeAfter = ({ image_1, image_2, ...props }: BeforeAfterProps) => {
  const root = useRef<HTMLDivElement>(null)
  const { motionRef } = useBeforeAfterMotion()
  const refs = mergeRefs(root, motionRef)
  const lastSize = useRef(0)

  useGSAP(
    ({ selector }) => {
      const target = selector?.('[data-draggable-target]')[0]
      const bounds = selector?.('[data-draggable-bounds]')[0]
      const trigger = selector?.('[data-draggable-trigger]')[0]
      const imageMask = selector?.('[data-draggable-image]')[0]
      const image = selector?.('[data-draggable-image]>img')[0]

      const setInitialPosition = () => {
        const x = bounds.clientWidth / 2
        updatePosition(x)
        gsap.set(target, { x })
        draggable.update()
      }

      const updatePosition = (value: number) => {
        const position = Math.max(0, Math.min(100, (value / bounds.clientWidth) * 100))
        gsap.set(imageMask, { xPercent: position })
        gsap.set(image, { xPercent: -position })
      }

      const draggable = Draggable.create(target, {
        type: 'x',
        bounds,
        trigger,
        inertia: true,
        lockAxis: true,
        maxDuration: 0.6,
        edgeResistance: 1,
        onThrowUpdate: () => {
          updatePosition(draggable.x)
        },
        onDrag: () => {
          updatePosition(draggable.x)
        }
      })[0]

      const resize = () => {
        if (bounds.clientWidth !== lastSize.current) {
          lastSize.current = bounds.clientWidth
          setInitialPosition()
        }
      }

      window.addEventListener('resize', resize)

      resize()

      return () => {
        window.removeEventListener('resize', resize)
        draggable.kill()
      }
    },
    { scope: root }
  )

  return (
    <Section ref={refs} {...props}>
      <Container className="overflow-hidden" data-draggable-trigger data-motion-block>
        <Grid className="relative aspect-[1675/1024]" data-draggable-bounds>
          <div className="relative col-[1/-1] row-[1/-1] overflow-clip">
            <Image
              src={image_1.url}
              alt={image_1.alt || 'Prima'}
              className="object-cover"
              draggable={false}
              fill
            />
          </div>
          <div className="relative col-[1/-1] row-[1/-1] overflow-clip" data-draggable-image>
            <Image
              src={image_2.url}
              alt={image_2.alt || 'Dopo'}
              className="object-cover"
              draggable={false}
              fill
            />
          </div>
          <div className="absolute left-0 top-0 h-full w-auto" data-draggable-target>
            <span
              className={cn(
                'absolute left-1/2 top-0 h-full w-[0.1rem] -translate-x-1/2 bg-white-light'
              )}
            />
            <button
              className={cn(
                'pointer-events-none',
                'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                'flex items-center justify-center space-x-spacing-3xs rounded-radius-rounded md:space-x-spacing-2xs',
                'text-black bg-white-light',
                'p-spacing-2xs md:p-spacing-xs'
              )}
            >
              <SVGIcon className="size-[1.6rem]" symbol="prev-icon" />
              <SVGIcon className="size-[1.6rem]" symbol="next-icon" />
            </button>
          </div>
        </Grid>
      </Container>
    </Section>
  )
}

export default BeforeAfter
